/**
 * Knowledge Base API Client
 * Handles all API calls for Knowledge Base entities
 */

import { BaseApiClient, ApiResponse } from './baseApiClient';
import type { StandardListRequest, GetByIdRequest } from '../types';

export interface KnowledgeBase {
  KnowledgeBaseID: number;
  Name: string;
  Description?: string;
  IsActive: boolean;
  CreatedOn?: string;
}

export class KnowledgeBaseApiClient extends BaseApiClient {
  /**
   * Get all active knowledge bases
   */
  async getActive(request: StandardListRequest = {}): Promise<ApiResponse<KnowledgeBase[]>> {
    const requestWithPagination = {
      PageInfo: {
        PageNumber: 1,
        PageSize: 1000,
      },
      ...request,
    };
    return this.postQuery<KnowledgeBase[]>('/api/v1/ai-agent/knowledge-bases/active', requestWithPagination);
  }

  /**
   * Get knowledge base by ID
   */
  async getById(request: GetByIdRequest): Promise<ApiResponse<KnowledgeBase>> {
    const webRequest = {
      ID: {
        ID: request.id,
      },
    };
    return this.postQuery<KnowledgeBase>('/api/v1/ai-agent/knowledge-bases/get-by-id', webRequest);
  }

  /**
   * Create new knowledge base
   */
  async create(kb: any): Promise<ApiResponse<KnowledgeBase>> {
    const response = await this.post<any>('/api/v1/ai-agent/knowledge-bases', kb);

    // Handle response structure - API returns id in response.id.id or response.data.Id.id
    if (response.success) {
      let knowledgeBaseId = null;

      // Try to extract ID from various possible locations
      const r = response as any;
      if (r.id?.id) {
        knowledgeBaseId = r.id.id;
      } else if (response.data?.id?.id) {
        knowledgeBaseId = response.data.id.id;
      } else if (response.data?.Id?.id) {
        knowledgeBaseId = response.data.Id.id;
      }

      if (knowledgeBaseId) {
        return {
          success: true,
          data: {
            KnowledgeBaseID: knowledgeBaseId,
            ...kb,
            IsActive: kb.IsActive ?? true,
          } as KnowledgeBase,
        };
      }
    }

    // If we couldn't extract the ID but still got success, return what we have
    if (response.success && !response.data?.KnowledgeBaseID) {
      return {
        success: true,
        data: {
          KnowledgeBaseID: 0, // Fallback - will be updated from actual data
          ...kb,
          IsActive: kb.IsActive ?? true,
        } as KnowledgeBase,
      };
    }

    return response;
  }

  /**
   * Update existing knowledge base
   */
  async update(id: number, kb: any): Promise<ApiResponse<KnowledgeBase>> {
    const webRequest = {
      Data: {
        KnowledgeBaseID: id,
        ...kb,
      },
    };
    return this.put<KnowledgeBase>(`/api/v1/ai-agent/knowledge-bases/${id}`, webRequest);
  }

  /**
   * Delete knowledge base
   */
  async deleteById(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      ID: {
        ID: id,
      },
    };
    return this.delete<void>(`/api/v1/ai-agent/knowledge-bases/${id}`, webRequest);
  }

  /**
   * Activate knowledge base
   */
  async activate(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      KnowledgeBaseID: {
        ID: id,
      },
    };
    return this.post<void>('/api/v1/ai-agent/knowledge-bases/activate', webRequest);
  }

  /**
   * Deactivate knowledge base
   */
  async deactivate(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      KnowledgeBaseID: {
        ID: id,
      },
    };
    return this.post<void>('/api/v1/ai-agent/knowledge-bases/deactivate', webRequest);
  }

  /**
   * Set knowledge base as default
   */
  async setAsDefault(id: number): Promise<ApiResponse<void>> {
    const webRequest = {
      KnowledgeBaseID: {
        ID: id,
      },
    };
    return this.post<void>('/api/v1/ai-agent/knowledge-bases/set-as-default', webRequest);
  }
}

export const knowledgeBaseApiClient = new KnowledgeBaseApiClient();
